import React from "react"
import { Helmet } from "react-helmet"
import { useLocation } from "@reach/router"
import { useStaticQuery, graphql } from "gatsby"

interface SEOProps {
  title?: string | null
  description?: string | null
  image?: string | null
  article?: boolean
  keywords?: string | null
}

interface SEOQueryData {
  site: {
    siteMetadata: {
      defaultTitle: string
      titleTemplate: string
      defaultDescription: string
      siteUrl: string
      defaultImage: string
      twitterUsername: string
    }
  }
}

const SEO: React.FC<SEOProps> = ({
  title = null,
  description = null,
  image = null,
  article = false,
  keywords = null,
}) => {
  const { pathname } = useLocation()
  const { site } = useStaticQuery<SEOQueryData>(query)

  const {
    defaultTitle,
    titleTemplate,
    defaultDescription,
    siteUrl,
    defaultImage,
    twitterUsername,
  } = site.siteMetadata

  const resolvedImage = image || defaultImage
  const isAbsoluteImage = /^https?:\/\//i.test(resolvedImage)

  const seo = {
    title: title || defaultTitle,
    description: description || defaultDescription,
    image: isAbsoluteImage ? resolvedImage : `${siteUrl}${resolvedImage}`,
    url: `${siteUrl}${pathname}`,
    keywords,
  }

  const isHome = pathname === "/" || pathname === ""

  const personLD = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Ayemun Hossain",
    alternateName: ["Ayemun", "AyemunHossain"],
    url: siteUrl,
    image: seo.image,
    jobTitle: "Senior Software Engineer",
    description: defaultDescription,
    sameAs: [
      "https://github.com/AyemunHossain",
      "https://www.linkedin.com/in/ayemunh/",
    ],
    knowsAbout: [
      "Backend Engineering",
      "Node.js",
      "NestJS",
      "Python",
      "Django",
      "Microservices",
      "Apache Kafka",
      "RabbitMQ",
      "gRPC",
      "Docker",
      "Kubernetes",
      "Terraform",
      "AWS",
      "Azure",
      "DevSecOps",
      "AI Engineering",
      "Retrieval-Augmented Generation",
      "Vector Search",
      "Live Streaming",
      "Distributed Systems",
    ],
  }

  const websiteLD = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: defaultTitle,
    url: siteUrl,
    description: defaultDescription,
    inLanguage: "en",
    author: { "@type": "Person", name: "Ayemun Hossain", url: siteUrl },
  }

  const articleLD = article
    ? {
        "@context": "https://schema.org",
        "@type": "TechArticle",
        headline: seo.title,
        name: seo.title,
        description: seo.description,
        image: seo.image,
        url: seo.url,
        keywords: keywords || undefined,
        inLanguage: "en",
        author: {
          "@type": "Person",
          name: "Ayemun Hossain",
          url: siteUrl,
        },
        publisher: {
          "@type": "Person",
          name: "Ayemun Hossain",
          url: siteUrl,
        },
        mainEntityOfPage: { "@type": "WebPage", "@id": seo.url },
      }
    : null

  return (
    <Helmet
      htmlAttributes={{ lang: "en" }}
      title={seo.title}
      titleTemplate={titleTemplate}
    >
      <meta name="description" content={seo.description} />
      <meta name="image" content={seo.image} />
      <meta name="author" content="Ayemun Hossain" />

      {seo.keywords && <meta name="keywords" content={seo.keywords} />}

      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
      <meta name="googlebot" content="index, follow" />

      {seo.url && <link rel="canonical" href={seo.url} />}

      {seo.url && <meta property="og:url" content={seo.url} />}
      <meta property="og:site_name" content={defaultTitle} />
      <meta property="og:locale" content="en_US" />

      {(article ? true : null) && <meta property="og:type" content="article" />}

      {seo.title && <meta property="og:title" content={seo.title} />}

      {seo.description && (
        <meta property="og:description" content={seo.description} />
      )}

      {seo.image && <meta property="og:image" content={seo.image} />}

      <meta name="twitter:card" content="summary_large_image" />

      {twitterUsername && (
        <meta name="twitter:creator" content={twitterUsername} />
      )}

      {seo.title && <meta name="twitter:title" content={seo.title} />}

      {seo.description && (
        <meta name="twitter:description" content={seo.description} />
      )}

      {seo.image && <meta name="twitter:image" content={seo.image} />}
      <meta property="og:image:alt" content={`${seo.title} — Ayemun Hossain`} />

      <script type="application/ld+json">
        {JSON.stringify(personLD)}
      </script>
      {isHome && (
        <script type="application/ld+json">
          {JSON.stringify(websiteLD)}
        </script>
      )}
      {articleLD && (
        <script type="application/ld+json">
          {JSON.stringify(articleLD)}
        </script>
      )}
    </Helmet>
  )
}

export default SEO

const query = graphql`
  query SEO {
    site {
      siteMetadata {
        defaultTitle: title
        titleTemplate
        defaultDescription: description
        siteUrl: url
        defaultImage: image
        twitterUsername
      }
    }
  }
`
